import { useAuth } from '@hobbytools/platform';
import { Banner } from '@hobbytools/ui';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useNext } from '../lib/next';
import { AuthLayout, linkClass } from './AuthLayout';

/**
 * Landing route for email links and OAuth. Supabase exchanges the `code` for a session on load (PKCE);
 * we wait for it, then continue to `next`. If the link was opened in a different browser the exchange
 * can't complete, but the email is still confirmed, so we point the user at sign-in.
 */
export function AuthCallback() {
  const { user, isLoading } = useAuth();
  const { go, query } = useNext();
  const [params] = useSearchParams();
  const [timedOut, setTimedOut] = useState(false);
  const linkError = params.get('error_description') ?? params.get('error');

  useEffect(() => {
    if (!isLoading && user) go();
  }, [isLoading, user, go]);

  useEffect(() => {
    const t = window.setTimeout(() => setTimedOut(true), 4000);
    return () => window.clearTimeout(t);
  }, []);

  if (linkError) {
    return (
      <AuthLayout title="That link didn't work">
        <Banner tone="danger">{linkError.replace(/\+/g, ' ')}</Banner>
        <p className="text-sm">
          Links expire and work once.{' '}
          <Link className={linkClass} to={`/sign-in${query}`}>
            Sign in
          </Link>{' '}
          or{' '}
          <Link className={linkClass} to="/reset-password">
            request a new link
          </Link>
          .
        </p>
      </AuthLayout>
    );
  }

  if (timedOut && !user) {
    return (
      <AuthLayout title="Almost there">
        <Banner>Your email is confirmed. Sign in on this device to continue.</Banner>
        <Link className={linkClass} to={`/sign-in${query}`}>
          Go to sign in
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Signing you in…">
      <p className="text-fg-muted" role="status">
        One moment.
      </p>
    </AuthLayout>
  );
}
